import { calendar, records, type CalendarDayEntry, type PersonalBestRecordItem } from '../services/api';
import { shiftDay } from './fitnessProgress';

export interface FitnessProgressResult {
  days: CalendarDayEntry[] | null;
  peaks: PersonalBestRecordItem[] | null;
  rampError: boolean;
  recordsError: boolean;
}

/** Split capped API responses before ranking. Never silently rank an incomplete day. */
export async function fetchFitnessPeaks(userId: number, start: string, end: string): Promise<PersonalBestRecordItem[]> {
  const response = await records.getHistory(userId, { start_date: start, end_date: end, best_only: 0, limit: 500 });
  const rows = response.data?.data;
  if (!Array.isArray(rows)) throw new Error('Invalid records response');
  if (rows.length < 500) return rows;
  if (start === end) throw new Error('Daily records response is incomplete');
  const days = Math.round((Date.parse(end) - Date.parse(start)) / 86400000);
  const middle = shiftDay(start, Math.floor(days / 2));
  const first = await fetchFitnessPeaks(userId, start, middle);
  const second = await fetchFitnessPeaks(userId, shiftDay(middle, 1), end);
  return [...new Map([...first, ...second].map(row => [row.id, row])).values()];
}

export async function loadFitnessProgress(userId: number, today: string): Promise<FitnessProgressResult> {
  const [history, peaks] = await Promise.allSettled([
    calendar.getCalendarData(userId, shiftDay(today, -35), today),
    fetchFitnessPeaks(userId, shiftDay(today, -27), today),
  ]);
  const days = history.status === 'fulfilled' && Array.isArray(history.value.data?.data) ? history.value.data.data : null;
  return { days, peaks: peaks.status === 'fulfilled' ? peaks.value : null,
    rampError: days === null, recordsError: peaks.status === 'rejected' };
}
